import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService, HttpMethod } from '../common/api.service';
import { CategoryMappingCsvUploadResult } from './category-mapping-csv-upload-result.model';
import { MembershipTypeEditModel } from '../member/member-edit.model';
import { MyCampApp } from '../common/enumerations';
import { Organization, TimeZone } from './organization.model';
import { OrganizationAdmin } from './organization-admin.model';
import { OrganizationAdminEditModel, OrganizationCreateModel } from './organization-create.model';
import { OrganizationAdminUpdateResult } from './organization-admin-update-result.model';
import { OrganizationEditModel } from './organization-edit.model';
import { OrganizationListItem } from './organization-list-Item.model';
import { OrganizationPhotoLimitResult } from './organization-photo-limit.model';
import { OrganizationRolloverModel } from './organization-rollover.model';
import { Album } from '../albums/album.model';
import { OrganizationRolloverResult } from './organization-rollover-result.model';

@Injectable({
    providedIn: 'root'
})
export class OrganizationApiService extends ApiService
{
    constructor(http: HttpClient)
    {
        super(http);
    }

    getOrganizations(sortField?: string, sortOrder?: MyCampApp.SortOrder, pageIndex = 0, pageSize = ApiService.defaultPageSize): Observable<OrganizationListItem[]>
    {
        return this.apiRequest<any>(this.getOrganizationsUrl(sortField, sortOrder, pageIndex, pageSize), HttpMethod.Get)
            .pipe(map((json: any) => json.Organizations.map((item: any) => new OrganizationListItem(item))));
    }

    getCurrentOrganization(): Observable<Organization>
    {
        return this.apiRequest<any>(this.currentOrganizationUrl(), HttpMethod.Get).pipe(map((json: any) => new Organization(json)));
    }

    getCurrentOrganizationImages()
    {
        return this.apiRequest<any>(this.currentOrganizationImagesUrl(), HttpMethod.Get);
    }

    createOrganization(model: OrganizationCreateModel)
    {
        return this.apiRequest<any>(this.createOrganizationUrl, HttpMethod.Post, JSON.stringify(model));
    }

    getOrganizationAdmins(): Observable<OrganizationAdmin[]>
    {
        return this.apiRequest<any>(this.getOrganizationAdminsUrl, HttpMethod.Get)
            .pipe(map((json: any) => json.map((item: any) => new OrganizationAdmin(item))));
    }

    createOrganizationAdmin(model: OrganizationAdminEditModel): Observable<OrganizationAdminUpdateResult>
    {
        return this.apiRequest<any>(this.createOrganizationAdminUrl, HttpMethod.Post, JSON.stringify(model))
            .pipe(map((json: any) => new OrganizationAdminUpdateResult(json)));
    }

    deleteOrganizationAdmin(id: number): Observable<any>
    {
        return this.apiRequest<any>(this.deleteOrganizationAdminUrl(id), HttpMethod.Delete);
    }

    updateOrganizationAdmin(model: OrganizationAdminEditModel): Observable<OrganizationAdminUpdateResult>
    {
        return this.apiRequest<any>(this.updateOrganizationAdminUrl, HttpMethod.Put, JSON.stringify(model))
            .pipe(map((json: any) => new OrganizationAdminUpdateResult(json)));
    }

    updateOrganizationAdminPassword(id: number, password: string): Observable<any>
    {
        return this.apiRequest<any>(this.updateOrganizationAdminPasswordUrl, HttpMethod.Put, JSON.stringify({ Id: id, Password: password }));
    }

    updateInfo(model: OrganizationEditModel): Observable<Organization>
    {
        return this.apiRequest<any>(this.updateOrganizationInfoUrl(), HttpMethod.Put, JSON.stringify(model))
            .pipe(map((json: any) => new Organization(json)));
    }

    updateRestrictedSettings(org: Organization): Observable<boolean>
    {
        const settings = {
            DataSyncType: org.DataSyncType,
            MaxPhotoCount: org.MaxPhotoCount,
            SmsYearlyLimit: org.SmsYearlyLimit,
            MaxMembers: org.MaxMembers,
            DefaultMemberType: org.DefaultMemberType,
            Group: org.Group,
            OptedOutForSignupConfirmationEmail: org.OptedOutForSignupConfirmationEmail,
            IsFaceRecognitionEnabled: org.IsFaceRecognitionEnabled,
            IsMMSEnabled: org.IsMMSEnabled,
            IsDocumentCenterEnabled: org.IsDocumentCenterEnabled,
            IsCheckInCheckOutEnabled: org.IsCheckInCheckOutEnabled,
            CheckInCheckOutValidationTypes: org.CheckInCheckOutValidationTypes,
            TimeForLastScheduledCheckout: org.TimeForLastScheduledCheckout,
            ProhibitLoginStartDate: org.ProhibitLoginStartDate,
            ProhibitLoginEndDate: org.ProhibitLoginEndDate,
        };
        return this.apiRequest<any>(this.updateOrganizationRestrictedSettingsUrl(), HttpMethod.Put, JSON.stringify(settings)).pipe(map( () => true));
    }

    setTheme(themeId: number)
    {
        return this.apiRequest<any>(this.updateOrganizationThemeUrl(), HttpMethod.Post, JSON.stringify({ ThemeId: themeId }));
    }

    setLogo(url: string)
    {
        return this.apiRequest<any>(this.updateOrganizationLogoUrl(), HttpMethod.Post, JSON.stringify({ Url: url }));
    }

    deleteLogo()
    {
        return this.apiRequest<any>(this.updateOrganizationLogoUrl(), HttpMethod.Delete, undefined);
    }

    setPhoto(url: string)
    {
        return this.apiRequest<any>(this.updateOrganizationPhotoUrl(), HttpMethod.Post, JSON.stringify({ Url: url }));
    }

    deletePhoto()
    {
        return this.apiRequest<any>(this.updateOrganizationPhotoUrl(), HttpMethod.Delete, undefined);
    }

    setWelcomeImage(url: string)
    {
        return this.apiRequest<any>(this.updateOrganizationWelcomeImageUrl(), HttpMethod.Post, JSON.stringify({ Url: url }));
    }

    deleteWelcomeImage()
    {
        return this.apiRequest<any>(this.updateOrganizationWelcomeImageUrl(), HttpMethod.Delete, undefined);
    }

    createMembershipType(model: MembershipTypeEditModel)
    {
        return this.apiRequest<any>(this.createMembershipTypeUrl(), HttpMethod.Post, JSON.stringify(model));
    }

    updateMembershipType(model: MembershipTypeEditModel)
    {
        return this.apiRequest<any>(this.updateOrDeleteMembershipTypeUrl(model.Id), HttpMethod.Put, JSON.stringify(model));
    }

    deleteMembershipType(id: number): Observable<any>
    {
        return this.apiRequest<any>(this.updateOrDeleteMembershipTypeUrl(id), HttpMethod.Delete, undefined);
    }

    getTimezonesList(): Observable<TimeZone[]>
    {
        return this.apiRequest<any>(this.getTimezonesUrl(), HttpMethod.Get).pipe(map((json: any[]) => json.map((item: any) => new TimeZone(item))));
    }

    checkPhotoLimit(sendEmail: boolean): Observable<OrganizationPhotoLimitResult>
    {
        return this.apiRequest<any>(this.checkPhotoLimitUrl(sendEmail), HttpMethod.Get);
    }

	rolloverOrganization(model: OrganizationRolloverModel): Observable<any>
	{
        return this.apiRequest<any>(this.rolloverOrganizationUrl, HttpMethod.Post, JSON.stringify(model))
	        .pipe(map((json: any) => new OrganizationRolloverResult(json)));
	}

	// =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================
    /* tslint:disable:member-ordering */

    private get baseOrganizationsApiUrl(): string { return `${this.baseApiUrl}organizations`; }
    getOrganizationsUrl(sortField?: string, sortOrder?: MyCampApp.SortOrder, pageIndex = 0, pageSize = ApiService.defaultPageSize): string
    {
        return `${this.baseOrganizationsApiUrl}?${this.getListQueryParams('name', pageIndex, pageSize, sortField, sortOrder)}`;
    }
    checkPhotoLimitUrl(sendEmail: boolean): string { return `${this.baseOrganizationsApiUrl}/checkPhotoLimit?sendEmail=${sendEmail ? 'true' : 'false'}`; }
    currentOrganizationImagesUrl(): string { return `${this.baseOrganizationsApiUrl}/current/images`; }
    currentOrganizationUrl(): string { return `${this.baseOrganizationsApiUrl}/current`; }
    get createOrganizationAdminUrl(): string { return `${this.baseOrganizationsApiUrl}/createAdmin`; }
    deleteOrganizationAdminUrl(id: number): string { return `${this.baseOrganizationsApiUrl}/deleteAdmin/${id}`; }
    get createOrganizationUrl(): string { return `${this.baseOrganizationsApiUrl}/create`; }
    get getOrganizationAdminsUrl(): string { return `${this.baseOrganizationsApiUrl}/admins`; }
    get updateOrganizationAdminUrl(): string { return `${this.baseOrganizationsApiUrl}/updateAdmin`; }
    get updateOrganizationAdminPasswordUrl(): string { return `${this.baseOrganizationsApiUrl}/updateAdminPassword`; }
    getTimezonesUrl(): string { return `${this.baseOrganizationsApiUrl}/timezones`; }
    updateOrganizationInfoUrl(): string { return this.baseOrganizationsApiUrl; }
    updateOrganizationLogoUrl(): string { return `${this.baseOrganizationsApiUrl}/logo`; }
    updateOrganizationPhotoUrl(): string { return `${this.baseOrganizationsApiUrl}/photo`; }
    updateOrganizationRestrictedSettingsUrl(): string { return `${this.baseOrganizationsApiUrl}/restricted`; }
    updateOrganizationThemeUrl(): string { return `${this.baseOrganizationsApiUrl}/theme`; }
    updateOrganizationWelcomeImageUrl(): string { return `${this.baseOrganizationsApiUrl}/welcomeImage`; }
    get createBaseMappingFileCsvUrl() { return `${this.baseOrganizationsApiUrl}/createBaseMappingFileCsv`; }
    get uploadCategoriesMappingCsvUrl() { return `${this.baseOrganizationsApiUrl}/uploadCategoryMappingCsv`; }
    get getCurrentMappingFileUrl() { return `${this.baseOrganizationsApiUrl}/getCategoryMapCsv`; }
    get rolloverOrganizationUrl(): string { return `${this.baseOrganizationsApiUrl}/rollover`; }

    private get baseMembershipTypesApiUrl(): string { return `${this.baseOrganizationsApiUrl}/memberTypes`; }
    createMembershipTypeUrl() { return this.baseMembershipTypesApiUrl; }
    updateOrDeleteMembershipTypeUrl(id: number) { return `${this.baseMembershipTypesApiUrl}/${id}`; }

    createBaseMappingFileCsv(file: File, ): Observable<CategoryMappingCsvUploadResult>
    {
        const formData = new FormData();
        formData.append('0', file, file.name);
        return this.apiUploadRequest<any>(this.createBaseMappingFileCsvUrl, formData, 600)
            .pipe(map(result => new CategoryMappingCsvUploadResult(result)));
    }

    uploadCategoriesMappingCsv(file: File, ): Observable<CategoryMappingCsvUploadResult>
    {
        const formData = new FormData();
        formData.append('0', file, file.name);
        return this.apiUploadRequest<any>(this.uploadCategoriesMappingCsvUrl, formData, 600)
            .pipe(map(result => new CategoryMappingCsvUploadResult(result)));
    }

    getCurrentMappingFile(): Observable<Response>
    {
        return this.apiGetRequestForCsv(this.getCurrentMappingFileUrl);
    }
}
